import type { IntegrationRequestDetail } from '../../../models/IntegrationRequest';
import type { IntegrationPrepareResult } from '../../../models/IntegrationRun';
import { HAS_INTEGRATION_TEST_ENVIRONMENT } from '@/utils';

const SHIPPING_PATH = /\/business\/integration\/shipping$/;

const SERVICE_DESCRIPTIONS: Record<string, string> = {
  'SAME-DAY-DELIVERY': 'Entrega a domicilio del cliente en el día.',
  INTERIOR: 'Envío al interior del país.',
};

const EXAMPLE_DESTINATIONS: Record<string, { state: string; city: string; street: string }> = {
  URUGUAY: { state: 'Montevideo', city: 'Montevideo', street: 'Av. 18 de Julio' },
  PERU: { state: 'Lima', city: 'Lima', street: 'Av. Javier Prado' },
};

export interface IntegrationDocData {
  commerceName: string;
  commerceCode: string;
  operatorCode: string;
  email: string;
  password: string;
  apiKey: string;
  apiSecret: string;
  loginUrl: string;
  prodApiBase: string;
  testApiBase: string | null;
  country: string;
  destination: { state: string; city: string; street: string };
  defaultBranch: { code: string; name: string };
  branches: { code: string; name: string }[];
  defaultServiceCode: string;
  services: { code: string; name: string; description: string }[];
}

export const buildIntegrationDocData = (
  request: IntegrationRequestDetail,
  prepare: IntegrationPrepareResult
): IntegrationDocData => {
  const apiBase = prepare.minimalIngestExample.createShipping.url
    .replace(SHIPPING_PATH, '')
    .replace(/\/test$/, '');
  const country = prepare.ingestDefaultsApplied.defaultOriginCountryName ?? 'URUGUAY';
  const defaultService =
    request.services.find((service) => service.isDefault) ?? request.services[0];

  return {
    commerceName: prepare.commerce.name,
    commerceCode: prepare.commerce.commerceCode,
    operatorCode: prepare.logisticOperator.logisticOperatorCode,
    email: prepare.integrationUser.email,
    password: prepare.minimalIngestExample.login.body.password,
    apiKey: prepare.credentials.apiKey,
    apiSecret: prepare.credentials.apiSecret,
    loginUrl: prepare.integrationUser.loginUrl,
    prodApiBase: apiBase,
    testApiBase: HAS_INTEGRATION_TEST_ENVIRONMENT ? `${apiBase}/test` : null,
    country,
    destination: EXAMPLE_DESTINATIONS[country] ?? EXAMPLE_DESTINATIONS.URUGUAY,
    defaultBranch: {
      code: prepare.defaultBranch.externalBranchCode ?? '1',
      name: prepare.defaultBranch.name,
    },
    branches: request.branches
      .filter((branch) => branch.externalBranchCode)
      .map((branch) => ({ code: branch.externalBranchCode as string, name: branch.name }))
      .sort((a, b) => Number(a.code) - Number(b.code)),
    defaultServiceCode: defaultService?.apiCode ?? 'SAME-DAY-DELIVERY',
    services: request.services.map((service) => ({
      code: service.apiCode ?? '',
      name: service.name,
      description: SERVICE_DESCRIPTIONS[service.apiCode ?? ''] ?? service.name,
    })),
  };
};
